import { memo, useCallback, useEffect, useState } from 'react'
import DataTable, {
  IDataTableConditionalRowStyles,
  IDataTableColumn,
} from 'react-data-table-component'
import LoaderMini from 'components/common/loaders/mini'
import {
  getStatusText,
  useStatusTranslation,
  getEncounterLastActivity,
} from 'services/Registrations'
import { useTranslation } from 'services/Translation'
import { CS, CS_m } from 'back-end-api'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { purple } from 'styles/Colors'
import CalendarIcon from 'assets/icons/Calendar'
import {
  setPatientSideBar,
  usePatientSideBarState,
  PatientSideBar,
  patientSideBarDefault,
} from 'services/Misc/patientSideBar'
import { useMiscDispatch } from 'services/Misc'
import { Container } from './Styles'
import texts from './Texts'

function partition<T>(predicate: (val: T) => boolean, arr: Array<T>): [Array<T>, Array<T>] {
  const partitioned: [Array<T>, Array<T>] = [[], []]
  arr.forEach((val: T) => {
    const partitionIndex: 0 | 1 = predicate(val) ? 0 : 1
    partitioned[partitionIndex].push(val)
  })
  return partitioned
}

const History = () => {
  const { t, formatDate } = useTranslation(texts)
  const tStatus = useStatusTranslation()
  const { registration } = usePatientRouteParams()
  const { data, isLoading } = CS.useRegistrationsList({
    without_old_closed_summaries: 'false',
  })
  const miscDispatch = useMiscDispatch()
  // Tie every encounter of the SAME patient together. A patient is identified
  // by `account_id`, which is stable across all of that patient's
  // registrations/summaries. We intentionally group on the patient rather than
  // on the currently viewed summary, so the list is the same no matter which of
  // the patient's summaries you have open. The currently viewed encounter is
  // kept in the list (labelled "Currently viewing") rather than excluded.
  const currentAccountId = registration?.account_id
  const patientRegistrations = data
    ?.filter((r: CS_m.ModelsRegistration) => {
      return (
        // `account_id === 0` means there is no real patient account behind the
        // registration. Grouping those together would mix unrelated (anonymous)
        // patients, so only group when we have a valid account id.
        !!currentAccountId &&
        r.account_id === currentAccountId &&
        r.summaries &&
        r.summaries.length > 0
      )
    })
    // List chronologically, most recent activity first, so the order is stable
    // and predictable regardless of the order the API returns rows in. We sort by
    // the same "last activity" date that is displayed in the row (and in search),
    // so the ordering always matches the visible dates.
    .sort((a: CS_m.ModelsRegistration, b: CS_m.ModelsRegistration) => {
      const aTime = getEncounterLastActivity(a)?.getTime() ?? new Date(a.created_at).getTime()
      const bTime = getEncounterLastActivity(b)?.getTime() ?? new Date(b.created_at).getTime()
      return bTime - aTime
    })

  const [ongoingRegistrations, pastRegistrations] = partition(
    (r: CS_m.ModelsRegistration): boolean => {
      return r.summaries?.every((s: CS_m.ModelsSummary) => !CS.isSummaryStatusFinal(s)) ?? false
    },
    patientRegistrations ?? []
  )

  const patientSideBarProps = usePatientSideBarState()
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<number | undefined>(
    patientSideBarProps.type === PatientSideBar.PastEncounter &&
      patientSideBarProps.pastEncounterProps?.currentRegistrationId === registration?.id
      ? patientSideBarProps.pastEncounterProps?.selectedRegistrationId
      : undefined
  )

  const handleRowClicked = useCallback(
    (row: CS_m.ModelsRegistration) => {
      // The encounter you're already viewing is shown in the list for context,
      // but clicking it would open a redundant "past encounter" side bar, so
      // it's a no-op.
      if (row.id === registration?.id) {
        return
      }
      if (selectedRegistrationId === row.id) {
        setSelectedRegistrationId(undefined)
        setPatientSideBar(miscDispatch, patientSideBarDefault)
        return
      }
      setSelectedRegistrationId(row.id)

      setPatientSideBar(miscDispatch, {
        type: PatientSideBar.PastEncounter,
        pastEncounterProps: {
          selectedRegistrationId: row.id,
          currentRegistrationId: registration?.id ?? 0,
        },
      })
    },
    [selectedRegistrationId, setSelectedRegistrationId, registration, miscDispatch]
  )

  // Unselect registration row when the past encounter side bar is closed
  useEffect(() => {
    if (
      !(
        patientSideBarProps.type === PatientSideBar.PastEncounter &&
        patientSideBarProps.pastEncounterProps?.currentRegistrationId === registration?.id
      )
    ) {
      setSelectedRegistrationId(undefined)
    }
  }, [patientSideBarProps, registration])

  const createConditionalRowStyles = useCallback((): IDataTableConditionalRowStyles<
    CS_m.ModelsRegistration
  >[] => {
    return [
      {
        when: (row: CS_m.ModelsRegistration) => {
          return row.id === selectedRegistrationId
        },
        style: {
          backgroundColor: 'rgba(108, 91, 255, 0.1)!important',
          borderRadius: '12px',
          borderBottom: 'none !important',
          position: 'relative',
          minHeight: 'auto',
          height: 'auto',
          alignItems: 'stretch',
          display: 'flex',
          flexDirection: 'row',
        },
      },
    ]
  }, [selectedRegistrationId])

  const cellContentWith = (
    row: CS_m.ModelsRegistration,
    content: string
  ) => (
    <div
      role="presentation"
      className="flex items-start w-full h-full font-csc65 pl-6 pr-6 py-4"
      style={{ 
        lineHeight: '1.4',
        wordWrap: 'break-word',
        overflowWrap: 'break-word'
      }}
      onClick={() => {
        handleRowClicked(row)
      }}
      onKeyDown={() => {
        handleRowClicked(row)
      }}
    >
      <CalendarIcon className="mr-6 mt-1 flex-shrink-0" />
      <span className="flex-1">{content}</span>
    </div>
  )

  const createColumns = (title: string): IDataTableColumn<CS_m.ModelsRegistration>[] => {
    return [
      {
        name: title,
        cell: (row: CS_m.ModelsRegistration) => {
          // Show the "last activity" date (matches the sidebar search results)
          // instead of the registration creation date. Fall back to created_at.
          const lastActivity = getEncounterLastActivity(row) ?? new Date(row.created_at)
          const dateLabel = formatDate(lastActivity, 'd MMMM, yyyy')
          const label =
            row.id === registration?.id
              ? `${dateLabel} (${t('currentlyViewing')})`
              : dateLabel
          return cellContentWith(row, label)
        },
      },
      {
        name: '',
        cell: (row: CS_m.ModelsRegistration) => {
          const summary = CS.getSummary(row)
          const complaint = summary?.complaint?.name ?? CS.SummaryType.GeneralMedicalQuestion
          return cellContentWith(row, complaint)
        },
      },
      {
        name: '',
        cell: (row: CS_m.ModelsRegistration) => {
          const summary = CS.getSummary(row)
          const redflags =
            summary?.red_flags && summary.red_flags.length > 0
              ? summary.red_flags.join(', ')
              : t('noRedFlags')
          return cellContentWith(row, redflags)
        },
      },
      {
        name: '',
        cell: (row: CS_m.ModelsRegistration) =>
          cellContentWith(row, getStatusText(row, tStatus)),
      },
      {
        name: '',
        cell: (row: CS_m.ModelsRegistration) => {
          if (row.id !== selectedRegistrationId) {
            return null
          }
          return (
            <>
              <div
                style={{
                  content: '" "',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: `${purple}`,
                  right: '-58px',
                  width: '50px',
                  height: '1px',
                  zIndex: 1,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  textAlign: 'center',
                  borderRadius: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: `${purple}`,
                  right: '-108px',
                  width: '50px',
                  height: '12px',
                  fontSize: '9px',
                  color: 'white',
                  zIndex: 1,
                }}
              >
                Selected
              </div>
            </>
          )
        },
        width: '0px',
      },
    ]
  }

  return (
    <Container height="30rem">
      <div className="ml-2 my-3 font-csc45">{t('content')}</div>
      {ongoingRegistrations.length > 0 && (
        <DataTable
          progressPending={isLoading}
          progressComponent={<LoaderMini center={undefined} />}
          columns={createColumns(t('ongoingTitle'))}
          data={[...ongoingRegistrations]}
          noHeader
          fixedHeader
          fixedHeaderScrollHeight="unset"
          dense
          conditionalRowStyles={createConditionalRowStyles()}
          onRowClicked={handleRowClicked}
        />
      )}
      {pastRegistrations.length > 0 && (
        <DataTable
          progressPending={isLoading}
          progressComponent={<LoaderMini center={undefined} />}
          columns={createColumns(t('pastTitle'))}
          data={[...pastRegistrations]}
          noHeader
          fixedHeader
          fixedHeaderScrollHeight="unset"
          dense
          conditionalRowStyles={createConditionalRowStyles()}
          onRowClicked={handleRowClicked}
        />
      )}
    </Container>
  )
}

History.whyDidYouRender = true

export default memo(History)
